import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Pencil, Trash2 } from "lucide-react";
import type { DataForwarderPolicy } from "../types/preferences_types";

interface DataForwarderPolicyDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policy: DataForwarderPolicy | null;
  canWrite: boolean;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}

export function DataForwarderPolicyDetailDialog({
  open,
  onOpenChange,
  policy,
  canWrite,
  onEdit,
  onDelete,
}: DataForwarderPolicyDetailDialogProps) {
  if (!policy) return null;

  const isComplete = policy.register_phase === 3;
  const phaseLabel = isComplete ? "등록 완료" : "등록 진행중";

  const destInfo = parseDestinationInfo(policy.destination_info);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{policy.policy_name}</span>
            <Badge variant={isComplete ? "success" : "warning"}>{phaseLabel}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <Section title="기본 정보">
            <Field label="ID" value={String(policy.id)} />
            <Field label="정책 이름" value={policy.policy_name} />
            <Field label="ORG ID" value={policy.org_id} />
            <Field label="Workspace ID" value={policy.workspace_id} />
          </Section>

          <Section title="필터 조건">
            <Field label="Channel" value={policy.channel} />
            <Field label="Sub Channel" value={policy.sub_channel} />
            <Field label="DW Account ID" value={policy.dw_account_id} />
            <Field label="전송 리포트" value={policy.reports} />
          </Section>

          <Section title="전송 설정">
            <Field
              label="전송 타입"
              value={policy.forwarding_type === "s3" ? "Amazon S3" : "Google Cloud Storage"}
            />
            {destInfo && (
              <div className="space-y-1.5">
                <span className="text-sm text-muted-foreground">Destination 정보</span>
                <div className="rounded-md border bg-muted/50 p-3">
                  <dl className="space-y-1.5 text-sm">
                    {Object.entries(destInfo).map(([key, value]) => (
                      <div key={key} className="flex gap-2">
                        <dt className="shrink-0 font-medium">{key}:</dt>
                        <dd className="break-all text-muted-foreground">{String(value ?? "")}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            )}
          </Section>
        </div>

        {canWrite && (
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="destructive"
              onClick={() => {
                onDelete(policy.id);
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              삭제
            </Button>
            <Button
              onClick={() => {
                onOpenChange(false);
                onEdit(policy.id);
              }}
            >
              <Pencil className="mr-2 h-4 w-4" />
              수정
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold text-foreground">{title}</h4>
      <div className="space-y-2 pl-1">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-baseline gap-4 text-sm">
      <span className="w-32 shrink-0 text-muted-foreground">{label}</span>
      <span className="break-all font-medium text-foreground">{value || "-"}</span>
    </div>
  );
}

function parseDestinationInfo(
  raw: Record<string, unknown> | string | null | undefined,
): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
}
